import { useMemo, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  Search,
  Warehouse,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useWavePlanningStore } from '@/stores/useWavePlanningStore'
import {
  REFERENCE_WEEKS,
  WAVE_CALENDARS,
  WAVE_FIXTURES,
} from '@/components/WavePlanning/data'
import { SKU_WAVE_FIXTURES } from '@/components/WavePlanning/sku-data'
import { calculateSkuPlanning } from '@/components/WavePlanning/sku-model'
import { shiftWaveDate } from '@/components/WavePlanning/model'
import {
  aggregateDcRows,
  dcUnits,
  dcDelta,
  calculateDcRow,
  demoDistributionCenters,
  equalDcSplit,
  type DcRow,
  type DcSplit,
} from '@/components/WavePlanning/dc-model'
import { WavePlanningNav } from '@/components/WavePlanning/WavePlanningNav'
import {
  getWavePlan,
  wavePlanPath,
  type WavePlan,
} from '@/components/WavePlanning/plans'
import { WavePlansBackLink } from '@/components/WavePlanning/WavePlansBackLink'
import { DcAllocationGrid } from '@/components/WavePlanning/DcAllocationGrid'
import { DcSplitDialog } from '@/components/WavePlanning/DcAllocationDialogs'
import '@/components/WavePlanning/dc-planning.css'

const dateLabel = (date: string) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${date}T00:00:00Z`))

export default function WavePlanningDistributionCenters() {
  const { planId } = useParams()
  const plan = getWavePlan(planId)
  if (!plan) return <Navigate to="/wave-planning" replace />
  return <DistributionPlanning key={plan.id} plan={plan} />
}

function DistributionPlanning({ plan }: { plan: WavePlan }) {
  const { calendarId, subclassId } = plan
  const state = useWavePlanningStore()
  const { setDcSplit, setWaveDcPercent, resetDcChanges } = state
  const calendar = WAVE_CALENDARS.find(item => item.id === calendarId)!
  const overview = WAVE_FIXTURES.find(item => item.id === subclassId)!
  const fixture = SKU_WAVE_FIXTURES.find(item => item.id === subclassId)!
  const scenario = `${calendarId}:${subclassId}`
  const overall = state.drafts[scenario] ?? overview.plannedWaves
  const skuDrafts = state.skuDrafts[scenario]
  const dcDrafts = state.dcDrafts[scenario]
  const centers = useMemo(
    () => demoDistributionCenters(fixture.dcCount),
    [fixture.dcCount]
  )
  const equalSplit = useMemo(() => equalDcSplit(centers), [centers])
  const weeks = useMemo(
    () =>
      REFERENCE_WEEKS.map(week => ({
        ...week,
        date: shiftWaveDate(week.date, calendar.offsetDays),
      })),
    [calendar.offsetDays]
  )
  const skuResult = useMemo(
    () => calculateSkuPlanning(fixture, weeks, overall, skuDrafts),
    [fixture, weeks, overall, skuDrafts]
  )
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null)
  const wave =
    skuResult.visibleWeeks.find(item => item.week === selectedWeek) ??
    skuResult.visibleWeeks[0]
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(0)
  const [allWaveTotals, setAllWaveTotals] = useState(false)
  const [showDcTotals, setShowDcTotals] = useState(false)
  const [copyRow, setCopyRow] = useState<DcRow | null>(null)
  const [notice, setNotice] = useState('')
  const allRows = useMemo(
    () =>
      skuResult.visibleWeeks.flatMap(week =>
        skuResult.rows
          .filter(row => row.waves[week.week].qty > 0)
          .map(row =>
            calculateDcRow(
              row,
              week.week,
              centers,
              dcDrafts?.[row.id]?.[week.week] ?? equalSplit
            )
          )
      ),
    [skuResult, centers, dcDrafts, equalSplit]
  )
  const waveRows = allRows.filter(row => row.week === wave?.week)
  const filteredRows = waveRows.filter(row => {
    const matches =
      `${row.line.sku} ${row.line.description} ${row.line.type} ${row.line.vendor}`
        .toLowerCase()
        .includes(search.trim().toLowerCase())
    return (
      matches &&
      (filter === 'all' ||
        (filter === 'split' && !row.balanced) ||
        (filter === 'source' && row.sourceNeedsReview) ||
        (filter === 'adjusted' &&
          (row.moqDelta > 0 || Math.abs(row.delta) > 1e-7)) ||
        (filter === 'over' && row.line.type !== 'Parent' && row.balance > 1e-7))
    )
  })
  const pageSize = 20
  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const currentPage = Math.min(page, pageCount - 1)
  const totals = aggregateDcRows(waveRows, centers)
  const dcTotals = aggregateDcRows(allWaveTotals ? allRows : waveRows, centers)
  const splitIssueCount = waveRows.filter(row => !row.balanced).length
  const changed = !!dcDrafts
  const splitFor = (row: DcRow): DcSplit =>
    Object.fromEntries(
      centers.map(dc => [dc.id, row.allocations[dc.id].percent])
    )
  const copyTargets = copyRow
    ? allRows.filter(row => row.id === copyRow.id && row.week !== copyRow.week)
    : []

  return (
    <div className="dc-planning flex min-w-0 flex-col gap-1.5 px-4 py-3">
      <header className="flex items-center justify-between gap-3">
        <div>
          <WavePlansBackLink />
          <h1 className="text-xl font-semibold">
            Distribution Center Planning
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {calendar.label}
            <span className="mx-2">/</span>
            <span className="font-medium text-foreground">
              {subclassId} · {plan.name}
            </span>
            <span className="mx-2">·</span>
            Demo destinations · {fixture.dcCount} DCs
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <p className="text-[11px] text-muted-foreground">
            Demo · Changes reset on reload
          </p>
          <Button
            size="sm"
            variant="outline"
            disabled={!changed}
            onClick={() => {
              resetDcChanges(calendarId, subclassId)
              setNotice('DC percentages reset for this subclass and calendar.')
            }}
          >
            <RotateCcw size={14} />
            Reset DC changes
          </Button>
        </div>
      </header>
      <WavePlanningNav planId={plan.id} compact />
      {!wave ? (
        <Card className="items-center gap-3 px-6 py-14 text-center shadow-none">
          <Warehouse size={30} className="text-muted-foreground" />
          <h2 className="text-sm font-semibold">No wave dates to distribute</h2>
          <p className="max-w-md text-xs text-muted-foreground">
            Add a wave in the overall plan, then assign SKU quantities before
            distributing them to DCs.
          </p>
          <Button asChild size="sm">
            <Link to={wavePlanPath(plan.id)}>Go to overall plan</Link>
          </Button>
        </Card>
      ) : (
        <>
          <Card
            className="flex gap-0 overflow-hidden py-0 shadow-none"
            aria-label="Selected wave totals"
          >
            <div className="grid min-w-0 flex-1 grid-cols-4">
              {[
                {
                  label: 'Planned wave units',
                  value: dcUnits(totals.waveQty),
                  hint: `${waveRows.length} SKU lines · ${dateLabel(wave.date)}`,
                  id: 'dc-planned',
                },
                {
                  label: 'Adjusted DC units',
                  value: dcUnits(totals.adjusted),
                  hint: `${dcDelta(totals.balance)} units net vs. wave`,
                  id: 'dc-adjusted',
                },
                {
                  label: 'Split issues',
                  value: String(splitIssueCount),
                  hint: splitIssueCount
                    ? 'Below / above 100% · View lines'
                    : 'All SKU lines allocated to 100%',
                  id: 'dc-split-issues',
                  warning: splitIssueCount > 0,
                  filter: 'split',
                },
                {
                  label: 'Excess units',
                  value: dcDelta(totals.excess),
                  hint: `${dcDelta(totals.excessPercent)}% of wave · ${totals.excessCount} lines`,
                  id: 'dc-excess',
                  warning: totals.excessCount > 0,
                  filter: 'over',
                },
              ].map(item => {
                const Stat = item.filter ? 'button' : 'div'
                return (
                  <Stat
                    key={item.id}
                    {...(item.filter
                      ? {
                          type: 'button' as const,
                          'aria-pressed': filter === item.filter,
                          onClick: () => {
                            setFilter(
                              filter === item.filter ? 'all' : item.filter!
                            )
                            setPage(0)
                          },
                        }
                      : {})}
                    className={`border-r border-border/60 px-4 py-2.5 text-left last:border-0 ${item.filter ? 'hover:bg-secondary/40 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary' : ''} ${item.filter && filter === item.filter ? 'bg-secondary/60 ring-1 ring-inset ring-primary' : ''}`}
                  >
                    <span className="block text-[11px] font-medium text-muted-foreground">
                      {item.label}
                    </span>
                    <span
                      data-testid={item.id}
                      className={`block mt-0.5 text-xl font-semibold tabular-nums ${item.warning ? 'text-amber-800' : ''}`}
                    >
                      {item.value}
                    </span>
                    <span className="block mt-0.5 text-[11px] text-muted-foreground">
                      {item.hint}
                    </span>
                  </Stat>
                )
              })}
            </div>
            <div className="flex shrink-0 items-center border-l px-3">
              <Button
                variant="outline"
                size="sm"
                aria-expanded={showDcTotals}
                aria-controls="dc-summary"
                onClick={() => setShowDcTotals(!showDcTotals)}
              >
                <Warehouse size={14} /> Totals by DC
                <ChevronDown
                  size={14}
                  className={showDcTotals ? 'rotate-180' : ''}
                />
              </Button>
            </div>
          </Card>
          {showDcTotals && (
            <section
              id="dc-summary"
              className="rounded-lg border bg-card"
              aria-label="Totals by distribution center"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
                <h2 className="flex items-center gap-2 text-xs font-semibold">
                  <Warehouse size={14} />
                  Totals by DC{' '}
                  <span className="font-normal text-muted-foreground">
                    · all SKU lines · excludes Parent
                  </span>
                </h2>
                <div
                  className="flex rounded-md bg-secondary p-0.5"
                  aria-label="DC totals scope"
                >
                  {[false, true].map(all => (
                    <button
                      key={String(all)}
                      aria-pressed={allWaveTotals === all}
                      className={`rounded px-2.5 py-1 text-[11px] ${allWaveTotals === all ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground'}`}
                      onClick={() => setAllWaveTotals(all)}
                    >
                      {all ? 'All waves' : 'Selected wave'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="dc-totals-grid" data-testid="dc-totals">
                {dcTotals.centers.map(dc => (
                  <div key={dc.id} className="px-4 py-3">
                    <p className="text-[11px] font-medium text-muted-foreground">
                      {dc.code}
                    </p>
                    <p className="mt-1 text-lg font-semibold tabular-nums">
                      {dcUnits(dc.adjusted)}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {dcUnits(dc.raw)} theoretical
                    </p>
                    <p
                      className={`mt-0.5 text-[10px] tabular-nums ${Math.abs(dc.adjusted - dc.raw) > 1e-7 ? 'text-amber-800' : 'text-muted-foreground'}`}
                    >
                      {dcDelta(dc.adjusted - dc.raw)} adjusted
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section
            aria-label="Wave selection"
            className="flex min-w-0 items-center gap-2 overflow-x-auto py-1"
          >
            <CalendarDays
              size={15}
              className="shrink-0 text-muted-foreground"
              aria-label="Receipt wave"
            />
            {skuResult.visibleWeeks.map((item, index) => (
              <button
                key={item.week}
                aria-pressed={wave.week === item.week}
                onClick={() => {
                  setSelectedWeek(item.week)
                  setPage(0)
                }}
                className={`dc-wave-tab ${wave.week === item.week ? 'dc-wave-tab-active' : ''}`}
              >
                <span className="font-semibold">Wave {index + 1}</span>
                <span>{dateLabel(item.date)}</span>
                {item.outside && <span>Outside plan</span>}
              </button>
            ))}
          </section>
          {wave.outside && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
            >
              <AlertTriangle size={15} />
              This date is outside the overall plan. Retained SKU quantities and
              DC allocations still count.
            </p>
          )}
          <Card className="min-w-0 gap-0 overflow-hidden py-0 shadow-none">
            <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
              <div>
                <h2 className="text-sm font-semibold">
                  SKU allocation for wave{' '}
                  {skuResult.visibleWeeks.indexOf(wave) + 1} -{' '}
                  {dateLabel(wave.date)}
                </h2>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Double-click % to edit · Theoretical →{' '}
                  <strong className="font-medium text-foreground">
                    Adjusted units
                  </strong>{' '}
                  · rounded up by case pack
                </p>
              </div>
              <div className="flex items-center gap-2">
                <label className="relative w-52">
                  <Search
                    size={14}
                    className="absolute left-2.5 top-2.5 text-muted-foreground"
                  />
                  <input
                    aria-label="Search SKU allocations"
                    placeholder="Search SKU or vendor…"
                    className="h-9 w-full rounded-md border bg-card pl-8 pr-3 text-xs outline-none focus:border-primary"
                    value={search}
                    onChange={event => {
                      setSearch(event.target.value)
                      setPage(0)
                    }}
                  />
                </label>
                <select
                  aria-label="Filter SKU allocations"
                  className="h-9 rounded-md border bg-card px-3 text-xs"
                  value={filter}
                  onChange={event => {
                    setFilter(event.target.value)
                    setPage(0)
                  }}
                >
                  <option value="all">All lines</option>
                  <option value="split">Split issues</option>
                  <option value="source">Wave allocation issues</option>
                  <option value="adjusted">Logistics adjustments</option>
                  <option value="over">Excess units</option>
                </select>
              </div>
            </div>
            <DcAllocationGrid
              key={wave.week}
              waveRows={waveRows}
              rows={filteredRows.slice(
                currentPage * pageSize,
                (currentPage + 1) * pageSize
              )}
              totalRows={filteredRows}
              centers={centers}
              waveLabel={dateLabel(wave.date)}
              onEdit={(row, dcId, value) =>
                setDcSplit(calendarId, subclassId, row.id, row.week, {
                  ...splitFor(row),
                  [dcId]: value,
                })
              }
              onEditAll={(dcId, value) => {
                setWaveDcPercent(
                  calendarId,
                  subclassId,
                  waveRows.map(row => row.id),
                  wave.week,
                  dcId,
                  value
                )
                setNotice(
                  `${dcUnits(value)}% applied to ${waveRows.length} SKU lines for ${centers.find(dc => dc.id === dcId)!.code} in ${dateLabel(wave.date)}.`
                )
              }}
              onCopy={setCopyRow}
            />
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-[11px] text-muted-foreground">
              <span>
                {filteredRows.length} of {waveRows.length} lines · Totals
                include all filtered pages, excluding Parent units.
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label="Previous SKU page"
                  disabled={currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeft size={14} />
                </Button>
                <span>
                  Page {currentPage + 1} of {pageCount}
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  aria-label="Next SKU page"
                  disabled={currentPage + 1 === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
            <p className="border-t bg-secondary/30 px-4 py-2 text-[11px] text-muted-foreground">
              MOQ applies per SKU/wave before the DC split. Each DC quantity
              rounds up to a multiple of the case pack size shown beside the
              SKU. Example: 700 units with a case pack of 24 → 720 units. Exact
              multiples and zero quantities stay unchanged.
            </p>
          </Card>
        </>
      )}
      <p
        role="status"
        aria-live="polite"
        className="text-xs text-muted-foreground"
      >
        {notice}
      </p>
      {copyRow && (
        <DcSplitDialog
          centers={centers}
          count={copyTargets.length}
          initial={splitFor(copyRow)}
          description={`${copyRow.line.sku} · ${copyRow.line.type}: ${copyTargets.length} other waves with units.`}
          onClose={() => setCopyRow(null)}
          onApply={split => {
            copyTargets.forEach(row =>
              setDcSplit(calendarId, subclassId, row.id, row.week, split)
            )
            setNotice(
              `DC split copied to ${copyTargets.length} other waves for ${copyRow.line.sku} ${copyRow.line.type}.`
            )
            setCopyRow(null)
          }}
        />
      )}
    </div>
  )
}
